import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import PlanningVersionForm from 'modules/time_planning/lib/forms/PlanningVersionForm'
import { usePlanningVersions } from 'modules/time_planning/store/collections/planningVersions'
import type PlanningVersion from 'modules/time_planning/store/models/PlanningVersion'

export interface Props {
  planningVersion: PlanningVersion
  onClose: () => void
}

const EditModal = ({ planningVersion, onClose }: Props) => {
  const planningVersions = usePlanningVersions()
  const employeeId = planningVersion.attributes.employee_id
  const defaultPlanningVersion = planningVersions.defaultPlanningVersion(employeeId)
  const minDate = defaultPlanningVersion.effectiveAt

  const createForm = (planningVersion: PlanningVersion) =>
    new PlanningVersionForm({
      ...planningVersion.toJS(),
      schedule_id: planningVersion.attributes.work_schedule_schedule_id,
      effective_at: dateBackendToUI(planningVersion.attributes.effective_at),
    })

  const [form, setForm] = React.useState(createForm(planningVersion))

  useEffect(() => {
    setForm(createForm(planningVersion))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningVersion.attributes.id])

  async function onSubmit() {
    const { success } = await asyncAction(() =>
      planningVersion.save({
        ...form.data(),
        effective_at: dateUIToBackend(form.get('effective_at').value),
      })
    )

    if (success) onClose()
  }

  return (
    <Modal
      onClose={onClose}
      onSubmit={() => onSubmit()}
      illustration="document-edit"
      title={i18n.t('time_planning.planning_versions.modal.update_date.title')}
      description={i18n.t('time_planning.planning_versions.modal.update_date.description')}
      footer={
        <Button submit>{i18n.t('time_planning.planning_versions.modal.update_date.button')}</Button>
      }
    >
      <Box gap="s24">
        <DayPicker
          label={i18n.t('time_planning.planning_versions.forms.effective_at')}
          field={form.get('effective_at')}
          minDate={minDate}
          pin
        />
        <Box
          paddingX="s24"
          paddingY="s16"
          background="champagne400"
          borderRadius={{
            all: 'abs008',
          }}
        >
          <Text.Normal>
            {i18n.t('time_planning.planning_versions.modal.update_date.warning')}
          </Text.Normal>
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(EditModal)
